/**
 * 左侧悬浮步骤导航卡片
 * 现代化设计，支持展开/收起
 */
import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

interface StepSidebarProps {
  steps: string[];
  currentStep: number;
  maxReachedStep: number; // 新增：最高到达步骤
  stepsCompleted: boolean[]; // 保留，用于进度计算
  onStepClick?: (step: number) => void;
  onExit?: () => void;
  onDeleteProject?: () => void; // 新增：删除项目回调
  canDelete?: boolean; // 新增：是否可以删除
  projectName?: string;
  alias?: string; // 新增：项目别名（优先显示）
  projectId?: string; // 新增：项目ID
  onProjectNameUpdate?: (name: string) => Promise<void>; // 新增：项目名称更新回调
  onCollapseChange?: (isCollapsed: boolean) => void;
  stepTimestamps?: { createdAt?: string; updatedAt?: string }[]; // 新增：步骤时间戳
  onShowToast?: (type: 'success' | 'error', title: string) => void; // 新增：Toast通知回调
}

const StepSidebar: React.FC<StepSidebarProps> = ({
  steps,
  currentStep,
  maxReachedStep, // 新增
  stepsCompleted,
  onStepClick,
  onExit,
  onDeleteProject, // 新增
  canDelete = true, // 新增，默认可以删除
  projectName = '标书项目',
  alias, // 新增
  projectId, // 新增
  onProjectNameUpdate, // 新增
  onCollapseChange,
  stepTimestamps = [], // 新增
  onShowToast, // 新增
}) => {
  // 显示名称：优先使用别名，没有则使用项目名称
  const displayName = alias?.trim() || projectName;
  // 步骤 1-4 的共享收缩状态（用关键字 'steps1-4' 存储）
  const [steps1to4Collapsed, setSteps1to4Collapsed] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('stepSidebarCollapseStates');
      if (saved) {
        const states = JSON.parse(saved);
        return states['steps1-4'] ?? false; // 默认展开
      }
    } catch {
      // 忽略错误
    }
    return false; // 默认展开
  });
  
  // 当前是否收缩
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // 步骤 5（索引 4）默认收缩
    if (currentStep === 4) {
      return true;
    }
    // 步骤 1-4 使用共享状态
    return steps1to4Collapsed;
  });

  // 动画状态：'idle' | 'collapsing' | 'expanding'
  const [animState, setAnimState] = useState<'idle' | 'collapsing' | 'expanding'>('idle');

  // 项目名称编辑状态
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState(displayName);
  const [isNameHovered, setIsNameHovered] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // 当displayName变化时，更新editingName
  useEffect(() => {
    setEditingName(displayName);
  }, [displayName]);

  // 监听步骤变化，更新收缩状态
  useEffect(() => {
    if (currentStep === 4) {
      // 步骤 5 默认收缩
      setIsCollapsed(true);
    } else {
      // 步骤 1-4 使用共享状态
      setIsCollapsed(steps1to4Collapsed);
    }
  }, [currentStep, steps1to4Collapsed]);

  // 切换收缩状态
  const handleToggleCollapse = (collapsed: boolean) => {
    if (collapsed) {
      // 开始收缩动画
      setAnimState('collapsing');
      setTimeout(() => {
        setIsCollapsed(true);
        setAnimState('idle');
      }, 250);
    } else {
      // 开始展开动画
      setAnimState('expanding');
      setIsCollapsed(false);
      setTimeout(() => {
        setAnimState('idle');
      }, 250);
    }
    
    // 如果当前是步骤 1-4，更新共享状态
    if (currentStep !== 4) {
      setSteps1to4Collapsed(collapsed);
      // 保存到 localStorage
      try {
        const saved = localStorage.getItem('stepSidebarCollapseStates');
        const states = saved ? JSON.parse(saved) : {};
        states['steps1-4'] = collapsed;
        localStorage.setItem('stepSidebarCollapseStates', JSON.stringify(states));
      } catch (e) {
        console.warn('保存侧边栏状态失败:', e);
      }
    }
    
    // 通知父组件
    onCollapseChange?.(collapsed);
  };

  // 删除确认状态
  const [isDeleting, setIsDeleting] = useState(false);

  // 处理删除按钮点击 - 直接调用父组件的删除回调（由父组件显示确认对话框）
  const handleDeleteClick = () => {
    if (!canDelete || !onDeleteProject || isDeleting) return;
    onDeleteProject();
  };

  // 开始编辑项目名称
  const handleStartEdit = () => {
    if (!projectId) return; // 快速创建页不允许编辑
    setIsEditingName(true);
    // 延迟聚焦，确保input已渲染
    setTimeout(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }, 0);
  };

  // 保存项目名称（实际保存的是别名alias）
  const handleSaveName = async () => {
    const trimmedName = editingName.trim();
    
    // 如果名称为空或未改变，恢复原名称
    if (!trimmedName || trimmedName === displayName) {
      setEditingName(displayName);
      setIsEditingName(false);
      return;
    }

    // 调用更新回调
    if (onProjectNameUpdate && projectId) {
      try {
        await onProjectNameUpdate(trimmedName);
        setIsEditingName(false);
        // 显示成功提示
        if (onShowToast) {
          onShowToast('success', '项目别名修改成功');
        }
      } catch (error) {
        console.error('更新项目别名失败:', error);
        // 显示错误提示
        if (onShowToast) {
          onShowToast('error', '项目别名修改失败');
        }
        // 恢复原名称
        setEditingName(displayName);
        setIsEditingName(false);
      }
    }
  };

  // 处理输入框失去焦点
  const handleBlur = () => {
    handleSaveName();
  };

  // 处理键盘事件
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveName();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingName(projectName);
      setIsEditingName(false);
    }
  };

  // 判断步骤状态（新逻辑）
  const getStepStatus = (stepIndex: number): 'current' | 'completed' | 'upcoming' => {
    if (stepIndex === maxReachedStep) {
      // 当前最高步骤：显示“进行中”
      return 'current';
    } else if (stepIndex < maxReachedStep) {
      // 已经走过的步骤：显示“已完成”
      return 'completed';
    } else {
      // 还没到达的步骤：显示“待开始”
      return 'upcoming';
    }
  };

  // 格式化时间显示
  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(/\//g, '-');
  };

  // 计算整体进度
  const progress = Math.round((stepsCompleted.filter(Boolean).length / steps.length) * 100);

  // 处理步骤点击（新逻辑）
  const handleStepClick = (stepIndex: number) => {
    // 只有 <= maxReachedStep 的步骤可以点击
    if (stepIndex <= maxReachedStep && onStepClick) {
      onStepClick(stepIndex);
    }
  };

  // 展开状态
  if (!isCollapsed) {
    const slideClass = animState === 'collapsing' ? 'animate-slideOutLeft' : animState === 'expanding' ? 'animate-slideInRight' : '';
    return (
      <div className={`fixed left-[1vw] bg-gray-100/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/80 flex flex-col z-[100] transition-all duration-300 max-h-[90vh] ${slideClass}`} style={{ width: 'clamp(160px, 11vw, 200px)', padding: 'clamp(12px, 1vw, 16px)', top: 0, bottom: 0, margin: 'auto 0', height: 'fit-content' }}>
        {/* 项目名称 - 可编辑 */}
        <div className="mb-4">
          <div className="flex items-center gap-2 text-gray-600">
            <svg className="flex-shrink-0" style={{ width: 'clamp(18px, 1.3vw, 22px)', height: 'clamp(18px, 1.3vw, 22px)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div 
              className="flex-1 flex items-center group min-w-0"
              onMouseEnter={() => setIsNameHovered(true)}
              onMouseLeave={() => setIsNameHovered(false)}
            >
              {isEditingName ? (
                <input
                  ref={nameInputRef}
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  className="flex-1 font-medium bg-gray-50 focus:bg-white rounded px-[3%] py-[2%] border-none outline-none transition-colors min-w-0"
                  style={{ fontSize: 'clamp(12px, 0.9vw, 14px)' }}
                  maxLength={100}
                />
              ) : (
                <>
                  <span className="font-medium truncate" style={{ fontSize: 'clamp(12px, 0.9vw, 14px)' }} title={displayName}>
                    {displayName}
                  </span>
                  {projectId && (
                    <button
                      onClick={handleStartEdit}
                      className={`flex-shrink-0 ml-1 p-0.5 text-gray-400 hover:text-blue-600 rounded transition-opacity ${
                        isNameHovered ? 'opacity-70' : 'opacity-0'
                      }`}
                      title="编辑项目名称"
                    >
                      <PencilIcon style={{ width: 'clamp(12px, 0.9vw, 14px)', height: 'clamp(12px, 0.9vw, 14px)' }} />
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* 分割线 */}
        <div className="px-[4%]">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        </div>

        {/* 步骤列表 */}
        <div className="flex-1 overflow-y-auto px-[3%] py-[3%] flex flex-col min-h-0" style={{ gap: 'clamp(8px, 2.5%, 12px)' }}>
          {steps.map((step, index) => {
            const status = getStepStatus(index);
            const isClickable = status === 'completed';

            return (
              <div
                key={index}
                onClick={() => handleStepClick(index)}
                className={`
                  relative rounded-xl transition-shadow duration-300
                  ${index === currentStep && status === 'completed'
                    ? 'bg-green-100 outline outline-2 outline-green-400 font-bold shadow-md'  // 当前选中的已完成
                    : index === currentStep && status === 'current'
                    ? 'bg-blue-100 outline outline-2 outline-blue-400 font-bold shadow-md'  // 当前选中的进行中
                    : index === currentStep
                    ? 'bg-gray-200 outline outline-2 outline-gray-400 font-bold shadow-md'  // 当前选中的待完成
                    : status === 'completed'
                    ? 'bg-green-100 border border-green-200'  // 已完成
                    : status === 'current'
                    ? 'bg-blue-100 border border-blue-200'  // 进行中
                    : 'bg-gray-200 border border-gray-300'  // 待完成
                  }
                  ${index <= maxReachedStep ? 'cursor-pointer hover:shadow-lg' : 'cursor-not-allowed opacity-50'}
                `}
                style={{ padding: 'clamp(8px, 3%, 16px)' }}>
                {/* 步骤序号和标题 */}
                <div className="flex items-center gap-[0.5vw]">
                  <div className={`
                    flex-shrink-0 rounded-full flex items-center justify-center font-semibold
                    ${index === currentStep && status === 'completed'
                      ? 'bg-green-500 text-white ring-2 ring-green-300'  // 当前选中的已完成
                    : index === currentStep && status === 'current'
                      ? 'bg-blue-500 text-white ring-2 ring-blue-300'  // 当前选中的进行中
                    : index === currentStep
                      ? 'bg-gray-500 text-white ring-2 ring-gray-300'  // 当前选中的待完成
                    : status === 'completed'
                      ? 'bg-green-500 text-white'  // 已完成
                      : status === 'current'
                      ? 'bg-blue-500 text-white'  // 进行中
                      : 'bg-gray-400 text-white'  // 待完成
                    }
                  `}
                  style={{ width: 'clamp(24px, 2vw, 32px)', height: 'clamp(24px, 2vw, 32px)', fontSize: 'clamp(11px, 0.8vw, 14px)' }}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium ${
                      index === currentStep
                        ? 'text-gray-900'  // 当前选中：深色加粗
                        : status === 'completed'
                        ? 'text-green-800'  // 已完成：深绿色
                        : status === 'current'
                        ? 'text-blue-800'  // 进行中：深蓝色
                        : 'text-gray-600'  // 待完成：灰色
                    }`}
                    style={{ fontSize: 'clamp(11px, 0.85vw, 14px)' }}>
                      {step}
                    </div>
                  </div>
                </div>

                {/* 状态文字和时间 */}
                <div className={`mt-[3%] ${
                  index === currentStep
                    ? 'text-gray-700 font-medium'  // 当前选中
                    : status === 'completed'
                    ? 'text-green-700'  // 已完成
                    : status === 'current'
                    ? 'text-blue-700'  // 进行中
                    : 'text-gray-500'  // 待完成
                }`}
                style={{ fontSize: 'clamp(10px, 0.75vw, 12px)' }}>
                  {status === 'completed' ? (
                    <div style={{ marginTop: '1%', marginBottom: '1%' }}>
                      <div>✓ 已完成</div>
                      {stepTimestamps[index] && (
                        <div style={{ fontSize: 'clamp(9px, 0.65vw, 11px)' }} className="opacity-80">
                          {stepTimestamps[index].updatedAt 
                            ? `修改: ${formatTimestamp(stepTimestamps[index].updatedAt)}`
                            : `创建: ${formatTimestamp(stepTimestamps[index].createdAt)}`
                          }
                        </div>
                      )}
                    </div>
                  ) : status === 'current' ? '→ 进行中' : '待开始'}
                </div>
              </div>
            );
          })}
        </div>

        {/* 分割线 */}
        <div className="px-[4%]">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        </div>

        {/* 进度显示 */}
        <div className="px-[4%] py-[3%]">
          <div className="flex items-center justify-between mb-[3%]" style={{ fontSize: 'clamp(12px, 0.9vw, 14px)' }}>
            <span className="text-gray-600 font-medium">整体进度</span>
            <span className="text-indigo-600 font-semibold">{progress}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* 分割线 */}
        <div className="px-[4%]">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
        </div>

        {/* 底部操作 */}
        <div className="px-[3%] py-[3%] space-y-[2%]">
          {/* 删除和退出按钮一排显示 */}
          <div className="flex gap-[1vw]">
            <button
              onClick={handleDeleteClick}
              disabled={!canDelete || isDeleting}
              title={canDelete ? '删除当前项目' : '快速创建页不可删除'}
              className={`flex-1 font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-[0.3vw]
                ${canDelete 
                  ? 'text-red-600 bg-red-50 hover:bg-red-100 border border-red-200' 
                  : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
                }
              `}
              style={{ padding: 'clamp(4px, 1.5%, 8px)', fontSize: 'clamp(10px, 0.75vw, 12px)' }}
            >
              {isDeleting ? (
                <div className="animate-spin rounded-full border-b-2 border-red-600" style={{ width: 'clamp(10px, 0.8vw, 12px)', height: 'clamp(10px, 0.8vw, 12px)' }}></div>
              ) : (
                <TrashIcon style={{ width: 'clamp(10px, 0.8vw, 12px)', height: 'clamp(10px, 0.8vw, 12px)' }} />
              )}
              删除
            </button>
            
            <button
              onClick={onExit}
              title="退出编辑"
              className="flex-1 font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 flex items-center justify-center gap-[0.3vw] border border-gray-200"
              style={{ padding: 'clamp(4px, 1.5%, 8px)', fontSize: 'clamp(10px, 0.75vw, 12px)' }}
            >
              <svg style={{ width: 'clamp(10px, 0.8vw, 12px)', height: 'clamp(10px, 0.8vw, 12px)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              退出
            </button>
          </div>
          
          {/* 收起按钮 */}
          <button
            onClick={() => handleToggleCollapse(true)}
            className="w-full text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors duration-200 flex items-center justify-center gap-[0.3vw]"
            style={{ padding: 'clamp(3px, 1%, 6px)', fontSize: 'clamp(10px, 0.75vw, 12px)' }}
          >
            <ChevronLeftIcon style={{ width: 'clamp(10px, 0.8vw, 12px)', height: 'clamp(10px, 0.8vw, 12px)' }} />
            收起
          </button>
        </div>
      </div>
    );
  }

  // 收起状态
  const slideClass = animState === 'expanding' ? 'animate-slideOutRight' : animState === 'collapsing' ? 'animate-slideInLeft' : '';
  return (
    <div 
      className={`fixed left-[1vw] bg-gray-100/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/80 flex flex-col items-center z-[100] transition-all duration-300 max-h-[90vh] ${slideClass}`}
      style={{ width: 'clamp(44px, 3.5vw, 56px)', padding: 'clamp(8px, 0.6vw, 12px)', top: 0, bottom: 0, margin: 'auto 0', height: 'fit-content' }}>
      {/* 步骤圆点 */}
      <div className="flex flex-col items-center justify-center" style={{ gap: '2px' }}>
        {steps.map((step, index) => {
          const status = getStepStatus(index);

          return (
            <div key={index} className="flex flex-col items-center">
              <div
                onClick={() => handleStepClick(index)}
                className={`
                  group relative rounded-full flex items-center justify-center font-bold transition-transform duration-300
                  ${index === currentStep && status === 'completed'
                    ? 'bg-green-100 text-green-600 outline outline-2 outline-green-400 shadow-lg'  // 当前选中的已完成
                    : index === currentStep && status === 'current'
                    ? 'bg-blue-100 text-blue-600 outline outline-2 outline-blue-400 shadow-lg'  // 当前选中的进行中
                    : index === currentStep
                    ? 'bg-gray-200 text-gray-600 outline outline-2 outline-gray-400 shadow-lg'  // 当前选中的待完成
                    : status === 'completed'
                    ? 'bg-green-500 text-white shadow-md'  // 已完成
                    : status === 'current'
                    ? 'bg-blue-500 text-white shadow-md'  // 进行中
                    : 'bg-gray-400 text-white shadow-sm'  // 待完成
                  }
                  ${index <= maxReachedStep ? 'cursor-pointer hover:scale-110' : 'cursor-not-allowed opacity-50'}
                `}
                style={{ width: 'clamp(28px, 2.2vw, 36px)', height: 'clamp(28px, 2.2vw, 36px)', fontSize: 'clamp(12px, 0.9vw, 14px)' }}
                title={`${index + 1}. ${step}`}
              >
                {index + 1}
                
                {/* Hover提示 */}
                {index <= maxReachedStep && (
                  <div className="absolute left-full ml-3 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                    {step}
                    {status === 'current' && <span className="ml-2 text-blue-300">进行中</span>}
                    {status === 'completed' && <span className="ml-2 text-green-300">✓</span>}
                    <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                  </div>
                )}
              </div>

              {/* 连接线 */}
              {index < steps.length - 1 && (
                <div className={`rounded-full ${
                  index < maxReachedStep
                    ? 'bg-gradient-to-b from-green-500 to-green-400' 
                    : index === maxReachedStep
                    ? 'bg-gradient-to-b from-blue-500 to-gray-300'
                    : 'bg-gray-300'
                }`} style={{ width: '2px', height: '18px', marginTop: '2px', marginBottom: '2px' }} />
              )}
            </div>
          );
        })}
      </div>

      {/* 展开按钮 */}
      <button
        onClick={() => handleToggleCollapse(false)}
        className="text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all duration-200"
        style={{ marginTop: '8px', padding: 'clamp(6px, 0.5vw, 10px)' }}
        title="展开"
      >
        <ChevronRightIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

export default StepSidebar;
